import { useState, useRef, useCallback, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, GripVertical, Save } from 'lucide-react';
import { productsApi } from '../../api/endpoints/products';
import { categoriesApi } from '../../api/endpoints/categories';
import { Button } from '../../components/ui/Button';
import { Select } from '../../components/ui/Select';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { EmptyState } from '../../components/ui/EmptyState';
import { useToast } from '../../components/ui/Alert';
import { getCdnUrl } from '../../lib/formatters';
import { useStore } from '../../contexts/StoreContext';

interface SortItem {
  _id: string;
  productId: string;
  title: string;
  image?: string;
  sort: number;
}

export function ProductSort() {
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { currentStore } = useStore();
  const [categoryId, setCategoryId] = useState('');
  const [items, setItems] = useState<SortItem[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const dragItem = useRef<number | null>(null);
  const dragOver = useRef<number | null>(null);

  const { data: categoriesData, isLoading: catsLoading } = useQuery({
    queryKey: ['categories', storeId],
    queryFn: () => categoriesApi.list(storeId!),
    enabled: !!storeId,
  });

  const { data: productsData, isLoading: productsLoading } = useQuery({
    queryKey: ['products-sort', storeId, categoryId],
    queryFn: () => productsApi.list(storeId!, { category: categoryId, limit: 200 }),
    enabled: !!storeId && !!categoryId,
  });

  useEffect(() => {
    if (!productsData) return;
    const sorted = (productsData.products || [])
      .map((p: any) => ({
        _id: p._id,
        productId: p.productId || p._id,
        title: p.title,
        image: p.images?.[0] || p.previewImage,
        sort: p.categorySorts?.[categoryId] ?? 999999,
      }))
      .sort((a: SortItem, b: SortItem) => a.sort - b.sort);
    setItems(sorted);
    setHasChanges(false);
  }, [productsData, categoryId]);

  const saveMutation = useMutation({
    mutationFn: () =>
      productsApi.saveSort(
        storeId!,
        categoryId,
        items.map((item, i) => ({ id: item.productId, sort: i })),
      ),
    onSuccess: () => {
      toast('success', t('common.success'));
      setHasChanges(false);
    },
    onError: () => toast('error', t('common.error')),
  });

  const handleDragStart = useCallback((index: number) => {
    dragItem.current = index;
  }, []);

  const handleDragEnter = useCallback((index: number) => {
    dragOver.current = index;
  }, []);

  const handleDragEnd = useCallback(() => {
    if (dragItem.current === null || dragOver.current === null) return;
    const from = dragItem.current;
    const to = dragOver.current;
    if (from === to) return;
    setItems((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
    setHasChanges(true);
    dragItem.current = null;
    dragOver.current = null;
  }, []);

  const categories = categoriesData?.categories ?? [];
  const isLoading = catsLoading || (categoryId && productsLoading);

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-6 flex items-center gap-3">
        <button
          onClick={() => navigate(`/${storeId}/products`)}
          className="rounded-lg p-2 text-text-muted hover:bg-bg-hover hover:text-text-primary"
        >
          <ArrowRight className="h-5 w-5" />
        </button>
        <h1 className="text-xl font-bold flex-1">{t('products.sortProducts', 'Sort Products')}</h1>
        {hasChanges && (
          <Button onClick={() => saveMutation.mutate()} isLoading={saveMutation.isPending}>
            <Save className="h-4 w-4" />
            {t('common.save')}
          </Button>
        )}
      </div>

      <div className="mb-6">
        <Select
          label={t('products.selectCategory', 'Select a category to reorder its products')}
          options={[
            { value: '', label: t('products.chooseCategory', '- Choose category -') },
            ...categories.map((c) => ({ value: c._id, label: c.name })),
          ]}
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
        />
      </div>

      {!categoryId ? (
        <EmptyState title={t('products.selectCategoryFirst', 'Select a category to start sorting')} />
      ) : isLoading ? (
        <LoadingSpinner />
      ) : items.length === 0 ? (
        <EmptyState title={t('common.noResults')} />
      ) : (
        <div className="space-y-1.5">
          {items.map((item, index) => (
            <div
              key={item._id}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragEnter={() => handleDragEnter(index)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => e.preventDefault()}
              className="flex items-center gap-3 rounded-lg border border-border-default bg-bg-secondary p-3 cursor-grab active:cursor-grabbing hover:border-accent/40 transition-colors"
            >
              <GripVertical className="h-5 w-5 text-text-muted shrink-0" />
              <span className="text-xs text-text-muted w-6 text-center shrink-0">{index + 1}</span>
              {item.image ? (
                <img
                  src={getCdnUrl(item.image, currentStore?._id)}
                  alt=""
                  className="h-10 w-10 rounded-lg object-cover shrink-0"
                />
              ) : (
                <div className="h-10 w-10 rounded-lg bg-bg-tertiary shrink-0" />
              )}
              <span className="text-sm font-medium flex-1 truncate">{item.title}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
